// src/components/games/zwepz.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Loader2, Coins, Flame, ChevronRight, Crosshair, 
  Zap, Trophy, Play, CircleDashed, User, Users, Bot, RefreshCw, ArrowLeft
} from 'lucide-react';
import * as THREE from 'three';
import { db } from '../../services/firebase';
import { 
  doc, setDoc, updateDoc, onSnapshot, 
  query, where, increment, getDoc, collection
} from 'firebase/firestore';

const GameStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,700;0,900;1,900&display=swap');
    
    .casino-font { font-family: 'Montserrat', sans-serif; }
    .scrollbar-hide::-webkit-scrollbar { display: none; }
    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    
    .text-glow-red { text-shadow: 0 0 15px rgba(220,38,38,0.9), 0 0 30px rgba(220,38,38,0.5); }
    .text-glow-gold { text-shadow: 0 0 15px rgba(255,215,0,0.9), 0 0 30px rgba(255,215,0,0.5); }
    
    @keyframes heartbeat {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .animate-heartbeat { animation: heartbeat 1s ease-in-out infinite; }
    
    .btn-push:active:not(:disabled) {
      transform: translateY(4px);
      border-bottom-width: 2px !important;
      margin-bottom: 4px;
    }
  `}</style>
);

class PremiumCasinoEngine {
  constructor(container: HTMLElement) {
    this.container = container;
    this.isSpinning = false;
    this.spinProgress = 0;
    this.targetSide = 'heads';
    this.hasImpacted = false;
    
    this.init();
  }

  init() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050000);
    this.scene.fog = new THREE.FogExp2(0x050000, 0.05);

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 150);
    this.camera.position.set(0, 6, 18);
    this.camera.lookAt(0, 1, 0);
    this.baseCameraY = 6;
    this.baseCameraZ = 18;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.container.appendChild(this.renderer.domElement);

    this.setupLighting();
    this.setupEnvironment();
    this.setupCoin();
    this.setupParticles();

    this.animate = this.animate.bind(this);
    this.animationId = requestAnimationFrame(this.animate);
  }

  setupLighting() {
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));

    this.mainSpot = new THREE.SpotLight(0xffffff, 40); 
    this.mainSpot.position.set(0, 60, 15); 
    this.mainSpot.angle = Math.PI / 4;
    this.mainSpot.penumbra = 0.5;
    this.mainSpot.decay = 2;
    this.mainSpot.distance = 120;
    this.mainSpot.castShadow = true;
    this.mainSpot.shadow.bias = -0.001;
    this.scene.add(this.mainSpot);

    const frontLight = new THREE.DirectionalLight(0xffffff, 3.0);
    frontLight.position.set(0, 10, 30);
    this.scene.add(frontLight);

    const goldRim = new THREE.PointLight(0xffd700, 25, 40); 
    goldRim.position.set(12, 10, -12);
    this.scene.add(goldRim);

    const redRim = new THREE.PointLight(0xff0000, 30, 40);
    redRim.position.set(-12, 10, 12);
    this.scene.add(redRim);

    this.impactLight = new THREE.PointLight(0xffffff, 0, 20);
    this.impactLight.position.set(0, 1, 0);
    this.scene.add(this.impactLight);
  }

  setupEnvironment() {
    const tableGeo = new THREE.PlaneGeometry(100, 100);
    const tableMat = new THREE.MeshStandardMaterial({ 
      color: 0x0a0000,
      roughness: 0.15, 
      metalness: 0.9,
    });
    const table = new THREE.Mesh(tableGeo, tableMat);
    table.rotation.x = -Math.PI / 2;
    table.receiveShadow = true;
    this.scene.add(table);

    const ringGeo = new THREE.RingGeometry(0.1, 0.3, 64);
    const ringMat = new THREE.MeshBasicMaterial({ 
      color: 0xff3333,
      transparent: true, 
      opacity: 0,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this.shockwave = new THREE.Mesh(ringGeo, ringMat);
    this.shockwave.rotation.x = -Math.PI / 2;
    this.shockwave.position.y = 0.02;
    this.scene.add(this.shockwave);
  }

  createPremiumTexture(text: string, isHeads: boolean) {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    
    const grad = ctx.createRadialGradient(512, 512, 0, 512, 512, 512);
    if (isHeads) {
      grad.addColorStop(0, '#FFDF00');
      grad.addColorStop(1, '#996600');
    } else {
      grad.addColorStop(0, '#FFD700');
      grad.addColorStop(1, '#8B6508');
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1024, 1024);
    
    ctx.beginPath();
    ctx.arc(512, 512, 480, 0, Math.PI * 2);
    ctx.lineWidth = 40;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(512, 512, 410, 0, Math.PI * 2);
    ctx.lineWidth = 15;
    ctx.setLineDash([25, 25]);
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#ffffff';
    ctx.font = '900 150px Montserrat, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 25;
    ctx.shadowOffsetY = 15;
    ctx.fillText(text, 512, 512);
    
    return new THREE.CanvasTexture(canvas);
  }

  setupCoin() {
    this.coinGroup = new THREE.Group();
    this.scene.add(this.coinGroup);

    const geo = new THREE.CylinderGeometry(2.5, 2.5, 0.5, 64);
    
    const edgeMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, 
      metalness: 0.6,
      roughness: 0.2, 
    });

    const headsMat = new THREE.MeshStandardMaterial({
      map: this.createPremiumTexture('HEADS', true),
      metalness: 0.3,
      roughness: 0.15,
      emissive: 0x332200, 
      emissiveIntensity: 1
    });

    const tailsMat = new THREE.MeshStandardMaterial({
      map: this.createPremiumTexture('TAILS', false),
      metalness: 0.3,
      roughness: 0.15,
      emissive: 0x332200,
      emissiveIntensity: 1
    });

    this.coin = new THREE.Mesh(geo, [edgeMat, headsMat, tailsMat]);
    this.coin.castShadow = true;
    this.coin.rotation.x = Math.PI / 2;
    
    this.coinGroup.add(this.coin);
    this.coinGroup.position.y = 2.6;
  }

  setupParticles() {
    const dustGeo = new THREE.BufferGeometry();
    const dustCount = 300;
    const dustPos = new Float32Array(dustCount * 3);
    for(let i=0; i < dustCount * 3; i++) {
      dustPos[i] = (Math.random() - 0.5) * 30;
    }
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({ 
      color: 0xff3333, size: 0.08, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending 
    });
    this.dust = new THREE.Points(dustGeo, dustMat);
    this.scene.add(this.dust);

    const sparkGeo = new THREE.BufferGeometry();
    const sparkCount = 100;
    this.sparkPos = new Float32Array(sparkCount * 3);
    this.sparkVel = [];
    for(let i=0; i<sparkCount; i++) {
      this.sparkPos[i*3] = 0;
      this.sparkPos[i*3+1] = 0;
      this.sparkPos[i*3+2] = 0;
      this.sparkVel.push(
        new THREE.Vector3((Math.random() - 0.5) * 4, Math.random() * 5, (Math.random() - 0.5) * 4)
      );
    }
    sparkGeo.setAttribute('position', new THREE.BufferAttribute(this.sparkPos, 3));
    const sparkMat = new THREE.PointsMaterial({ 
      color: 0xffd700, size: 0.15, transparent: true, opacity: 0, blending: THREE.AdditiveBlending 
    });
    this.sparks = new THREE.Points(sparkGeo, sparkMat);
    this.scene.add(this.sparks);
  }

  spin(winningSide: string) {
    this.targetSide = winningSide;
    this.isSpinning = true;
    this.spinProgress = 0;
    this.hasImpacted = false;
    
    this.coinGroup.position.y = 2.6;
    this.coin.rotation.x = Math.PI / 2;
    
    this.spinSpeedX = 80 + Math.random() * 20; 
    this.spinSpeedZ = (Math.random() - 0.5) * 15;
  }

  triggerImpact() {
    this.hasImpacted = true;
    
    this.impactLight.intensity = 100;
    this.impactLight.color.setHex(0xff0000);
    
    this.shockwave.scale.set(1, 1, 1);
    this.shockwave.material.opacity = 1;

    this.sparks.material.opacity = 1;
    for(let i=0; i<100; i++) {
      this.sparkPos[i*3] = 0;
      this.sparkPos[i*3+1] = 0.5;
      this.sparkPos[i*3+2] = 0;
      this.sparkVel[i].set((Math.random() - 0.5) * 6, Math.random() * 7, (Math.random() - 0.5) * 6);
    }
    this.sparks.geometry.attributes.position.needsUpdate = true;
  }

  animate() {
    this.animationId = requestAnimationFrame(this.animate);
    const time = Date.now() * 0.001;
    
    this.dust.rotation.y = time * 0.02;
    this.dust.position.y = Math.sin(time * 0.3) * 0.5;

    if (this.isSpinning) {
      let step = 0.0035;
      if (this.spinProgress > 0.4 && this.spinProgress < 0.7) {
        step = 0.001;
      } else if (this.spinProgress >= 0.7) {
        step = 0.0015;
      }
      this.spinProgress += step; 
      
      const apexFactor = Math.sin(this.spinProgress * Math.PI);
      const height = apexFactor * 28;
      this.coinGroup.position.y = 2.5 + Math.max(0, height);
      
      const easeOut = 1 - Math.pow(1 - this.spinProgress, 4);
      const currentSpeed = this.spinSpeedX * (1 - easeOut);
      
      this.coin.rotation.x += currentSpeed * 0.1;
      this.coin.rotation.z += this.spinSpeedZ * 0.1 * (1 - easeOut);

      this.camera.position.y = this.baseCameraY + height * 0.25;
      this.camera.position.z = this.baseCameraZ + (apexFactor * 2);
      this.camera.lookAt(0, this.coinGroup.position.y * 0.9, 0);

      if (this.spinProgress >= 0.96 && !this.hasImpacted) {
        this.triggerImpact();
      }

      let shake = 0;
      if (this.spinProgress > 0.95) shake = (1 - this.spinProgress) * 15.0; 
      this.camera.position.x = (Math.random() - 0.5) * shake;
      this.camera.position.y += (Math.random() - 0.5) * shake * 0.5;

      if (this.spinProgress >= 1) {
        this.isSpinning = false;
        this.coinGroup.position.y = 2.5;
        this.camera.position.set(0, this.baseCameraY, this.baseCameraZ);
        this.camera.lookAt(0, 0, 0);
        
        const currentRot = this.coin.rotation.x;
        const normalizedRot = Math.round(currentRot / (Math.PI * 2)) * (Math.PI * 2);
        
        if (this.targetSide === 'heads') {
          this.coin.rotation.x = normalizedRot + (Math.PI / 2);
        } else {
          this.coin.rotation.x = normalizedRot - (Math.PI / 2);
        }
        this.coin.rotation.z = 0;
      }
    } else {
      if (!this.hasImpacted) {
        this.coinGroup.position.y = 2.6 + Math.sin(time * 2) * 0.1;
        this.coin.rotation.z = Math.sin(time) * 0.1;
      }
    }

    if (this.hasImpacted) {
      this.impactLight.intensity *= 0.8; 
      this.shockwave.scale.addScalar(0.9);
      this.shockwave.material.opacity *= 0.85;

      if (this.sparks.material.opacity > 0.01) {
        this.sparks.material.opacity *= 0.92;
        const positions = this.sparks.geometry.attributes.position.array;
        for(let i=0; i<100; i++) {
          positions[i*3] += this.sparkVel[i].x * 0.1;
          positions[i*3+1] += this.sparkVel[i].y * 0.1;
          positions[i*3+2] += this.sparkVel[i].z * 0.1;
          this.sparkVel[i].y -= 0.15; 
          
          if (positions[i*3+1] < 0) {
            positions[i*3+1] = 0;
            this.sparkVel[i].y *= -0.5;
          }
        }
        this.sparks.geometry.attributes.position.needsUpdate = true;
      }
    }

    this.renderer.render(this.scene, this.camera);
  }

  cleanup() {
    cancelAnimationFrame(this.animationId);
    if (this.renderer) this.renderer.dispose();
  }
}

interface ZwepzProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  updateProfile: (data: any) => Promise<void>;
  onBuyCredits: () => void;
}

export const Zwepz: React.FC<ZwepzProps> = ({ 
  isOpen, 
  onClose, 
  currentUser, 
  updateProfile,
  onBuyCredits 
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<PremiumCasinoEngine | null>(null);
  
  const [gameMode, setGameMode] = useState('solo'); 
  const [activeMatchId, setActiveMatchId] = useState<string | null>(null);
  const [match, setMatch] = useState<any>(null);
  const [lobbies, setLobbies] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [wager, setWager] = useState('50');
  const [selectedSide, setSelectedSide] = useState('heads');
  const [view, setView] = useState('lobby'); 
  const [credits, setCredits] = useState(0);

  const uid = currentUser?.uid;
  const username = currentUser?.displayName || `VIP_${uid?.substring(0,4)}`;

  // 🟢 FETCH USER CREDITS
  useEffect(() => {
    if (currentUser?.cred !== undefined) {
      setCredits(currentUser.cred);
    }
  }, [currentUser]);

  // 🟢 UPDATE CREDITS HELPER
  const updateCredits = async (amount: number) => {
    if (!uid) return;
    const newBalance = credits + amount;
    setCredits(newBalance);
    await updateProfile({ cred: newBalance });
  };

  useEffect(() => {
    if (!uid || activeMatchId || gameMode !== 'multiplayer') return;
    const q = query(collection(db, 'hype_flips'), where('status', '==', 'waiting'));
    const unsub = onSnapshot(q, (snap) => {
      const allLobbies = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLobbies(allLobbies.filter(l => l.status === 'waiting'));
    });
    return () => unsub();
  }, [uid, activeMatchId, gameMode]);

  useEffect(() => {
    if (!activeMatchId || !uid || gameMode !== 'multiplayer') return;
    const unsub = onSnapshot(doc(db, 'hype_flips', activeMatchId), (docSnap) => {
      if (docSnap.exists()) {
        const matchData = { id: docSnap.id, ...docSnap.data() };
        setMatch(matchData);
        if (matchData.status === 'cancelled') {
          processCancelled(matchData);
        } else if (matchData.status === 'playing' && view !== 'flipping' && view !== 'matchup') {
          triggerMatchup(matchData);
        }
      } else {
        setActiveMatchId(null);
        setMatch(null);
        setView('lobby');
      }
    });
    return () => unsub();
  }, [activeMatchId, uid, view, gameMode]);

  useEffect(() => {
    if ((view === 'flipping' || view === 'result' || view === 'matchup' || view === 'waiting') && mountRef.current && !engineRef.current) {
      engineRef.current = new PremiumCasinoEngine(mountRef.current);
    }
    if (view === 'lobby' && engineRef.current) {
      engineRef.current.cleanup();
      engineRef.current = null;
    }
  }, [view]);

  if (!isOpen) return null;

  const handleSoloPlay = async () => {
    if (!uid) return;
    const bet = parseInt(wager);
    if (isNaN(bet) || bet <= 0 || credits < bet) return;

    setIsProcessing(true);
    try {
      await updateCredits(-bet);

      let winProb = 0.48;
      if (bet > 2000) winProb = 0.15;
      else if (bet > 500) winProb = 0.25;
      else if (bet > 100) winProb = 0.40;

      const isWin = Math.random() <= winProb;
      const resultSide = isWin ? selectedSide : (selectedSide === 'heads' ? 'tails' : 'heads');
      const winnerId = isWin ? uid : 'house';

      const soloMatchData = {
        id: `solo_${Date.now()}`,
        creatorId: uid, creatorName: username, creatorSide: selectedSide,
        joinerId: 'house', joinerName: 'CASINO VAULT', joinerSide: selectedSide === 'heads' ? 'tails' : 'heads',
        wagerAmount: bet, pot: bet * 2,
        resultSide: resultSide, winnerId: winnerId,
        isSolo: true, payoutClaimed: false
      };

      setMatch(soloMatchData);
      setActiveMatchId(soloMatchData.id);
      
      triggerMatchup(soloMatchData);
    } catch (e) { console.error(e); }
    setIsProcessing(false);
  };

  const handleCreateMatch = async () => {
    if (!uid) return;
    const bet = parseInt(wager);
    if (isNaN(bet) || bet <= 0 || credits < bet) return;
    setIsProcessing(true);
    try {
      await updateCredits(-bet);
      const matchId = `flip_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const newMatch = {
        creatorId: uid, creatorName: username, wagerAmount: bet, creatorSide: selectedSide,
        status: 'waiting', createdAt: Date.now(), pot: bet * 2, isSolo: false
      };
      await setDoc(doc(db, 'hype_flips', matchId), newMatch);
      setActiveMatchId(matchId);
      setView('waiting');
    } catch (e) { console.error(e); }
    setIsProcessing(false);
  };

  const handleJoinMatch = async (matchToJoin: any) => {
    if (!uid || credits < matchToJoin.wagerAmount) return;
    setIsProcessing(true);
    try {
      await updateCredits(-matchToJoin.wagerAmount);
      const isHeads = Math.random() > 0.5;
      const resultSide = isHeads ? 'heads' : 'tails';
      const winnerId = matchToJoin.creatorSide === resultSide ? matchToJoin.creatorId : uid;

      const updatedGame = {
        joinerId: uid, joinerName: username, joinerSide: matchToJoin.creatorSide === 'heads' ? 'tails' : 'heads',
        status: 'playing', resultSide: resultSide, winnerId: winnerId, payoutClaimed: false
      };
      await updateDoc(doc(db, 'hype_flips', matchToJoin.id), updatedGame);
      setActiveMatchId(matchToJoin.id);
    } catch (e) { console.error(e); }
    setIsProcessing(false);
  };

  const processCancelled = async (matchData: any) => {
    if (matchData.creatorId === uid && !matchData.refunded) {
      await updateCredits(matchData.wagerAmount);
      await updateDoc(doc(db, 'hype_flips', matchData.id), { refunded: true });
    }
    setActiveMatchId(null);
    setMatch(null);
    setView('lobby');
  };

  const handleCancelLobby = async () => {
    if (!match || match.creatorId !== uid || match.status !== 'waiting') return;
    await updateDoc(doc(db, 'hype_flips', match.id), { status: 'cancelled' });
  };

  const claimPayout = async (currentMatch: any) => {
    const targetMatch = currentMatch || match;
    if (!targetMatch || targetMatch.winnerId !== uid || targetMatch.payoutClaimed) return;
    try {
      if (!targetMatch.isSolo) await updateDoc(doc(db, 'hype_flips', targetMatch.id), { payoutClaimed: true });
      await updateCredits(targetMatch.pot);
    } catch (e) { console.error(e); }
  };

  const triggerMatchup = (gameData: any) => {
    setView('matchup');
    setTimeout(() => {
      setView('flipping');
      if (engineRef.current) engineRef.current.spin(gameData.resultSide);
      
      setTimeout(() => {
        setView('result');
        if (gameData.winnerId === uid) claimPayout(gameData);
      }, 10500); 
      
    }, 2500);
  };

  const renderLobby = () => (
    <div className="flex flex-col h-full overflow-y-auto p-4 md:p-8 lg:p-12 space-y-8 w-full max-w-7xl mx-auto relative z-20 pointer-events-auto custom-scrollbar">
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-2">
        <div>
          <h1 className="text-5xl sm:text-7xl font-black italic tracking-tighter text-white leading-none uppercase drop-shadow-[0_0_20px_rgba(255,0,0,0.4)]">
            STAKE<span className="text-red-600">FLIP</span>
          </h1>
          <p className="text-zinc-500 uppercase tracking-[0.4em] text-[10px] sm:text-xs mt-2 font-bold">High Roller VIP Table</p>
        </div>

        <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
          <div className="bg-[#050505] border border-red-900/30 p-1.5 rounded-2xl flex gap-1 shadow-inner">
            <button 
              onClick={() => setGameMode('solo')}
              className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${gameMode === 'solo' ? 'bg-gradient-to-b from-red-900 to-black text-white shadow-[0_2px_15px_rgba(220,38,38,0.4)] border-t border-red-600 border-b-2 border-b-black' : 'text-zinc-600 hover:text-white'}`}
            >
              <Bot size={16} /> Solo
            </button>
            <button 
              onClick={() => setGameMode('multiplayer')}
              className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${gameMode === 'multiplayer' ? 'bg-gradient-to-b from-red-900 to-black text-white shadow-[0_2px_15px_rgba(220,38,38,0.4)] border-t border-red-600 border-b-2 border-b-black' : 'text-zinc-600 hover:text-white'}`}
            >
              <Users size={16} /> VS Players
            </button>
          </div>

          <div className="bg-gradient-to-b from-[#180000] to-[#090000] border-t border-red-900/50 border-b-2 border-b-black p-3 sm:p-4 rounded-2xl min-w-[120px] text-right shadow-2xl">
            <span className="text-[9px] text-zinc-500 uppercase tracking-widest font-bold">Vault Balance</span>
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums tracking-tighter">{credits.toLocaleString()} <span className="text-xs text-red-600">CRD</span></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        <div className="lg:col-span-5 bg-[#050505]/90 backdrop-blur-2xl border border-red-900/30 rounded-[2rem] p-6 sm:p-8 shadow-[0_20px_50px_rgba(220,38,38,0.15)] relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent opacity-50"></div>
          
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-black italic uppercase tracking-widest text-white flex items-center gap-3">
              <Crosshair size={20} className="text-red-500"/> Setup Wager
            </h3>
            <span className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-red-900/20 text-red-500 border border-red-900/50 shadow-[0_0_10px_rgba(220,38,38,0.2)]">
              {gameMode === 'solo' ? 'House Flip' : 'PVP Lobby'}
            </span>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase block mb-2 font-bold tracking-widest">Amount (CRD)</label>
              <div className="relative group">
                <input 
                  type="number" 
                  value={wager} 
                  onChange={e => setWager(e.target.value)} 
                  className="relative w-full bg-gradient-to-b from-black to-[#0a0000] border-2 border-zinc-900 rounded-2xl p-4 sm:p-5 text-white font-black text-3xl outline-none focus:border-red-600 transition-all shadow-inner" 
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1.5">
                  <button 
                    onClick={() => setWager(Math.floor(credits / 2).toString())} 
                    className="px-3 py-2 bg-gradient-to-b from-[#27272a] to-[#18181b] border-t border-[#3f3f46] border-b-2 border-black rounded-xl text-xs font-black text-zinc-400 hover:text-white btn-push transition-all"
                  >
                    1/2
                  </button>
                  <button 
                    onClick={() => setWager(credits.toString())} 
                    className="px-3 py-2 bg-gradient-to-b from-[#ff3333] to-[#cc0000] border-t border-[#ff6666] border-b-2 border-[#800000] rounded-xl text-xs font-black text-white hover:brightness-110 btn-push transition-all shadow-[0_0_15px_rgba(220,38,38,0.4)]"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="text-[11px] text-zinc-500 uppercase block mb-2 font-bold tracking-widest">Select Your Side</label>
              <div className="flex gap-4">
                <button 
                  onClick={() => setSelectedSide('heads')} 
                  className={`flex-1 py-5 rounded-2xl font-black uppercase tracking-widest transition-all duration-200 btn-push ${selectedSide === 'heads' ? 'bg-gradient-to-b from-[#ff3333] to-[#cc0000] text-white border-t-2 border-[#ff8888] border-x border-x-[#990000] border-b-[6px] border-b-[#660000] shadow-[0_6px_20px_rgba(220,38,38,0.5)]' : 'bg-gradient-to-b from-[#18181b] to-black text-zinc-500 border-t-2 border-[#27272a] border-x border-x-black border-b-[6px] border-b-[#050505] shadow-[0_6px_15px_rgba(0,0,0,0.5)] hover:brightness-110'}`}
                >
                  <span className="text-lg">Heads</span>
                </button>
                <button 
                  onClick={() => setSelectedSide('tails')} 
                  className={`flex-1 py-5 rounded-2xl font-black uppercase tracking-widest transition-all duration-200 btn-push ${selectedSide === 'tails' ? 'bg-gradient-to-b from-[#ff3333] to-[#cc0000] text-white border-t-2 border-[#ff8888] border-x border-x-[#990000] border-b-[6px] border-b-[#660000] shadow-[0_6px_20px_rgba(220,38,38,0.5)]' : 'bg-gradient-to-b from-[#18181b] to-black text-zinc-500 border-t-2 border-[#27272a] border-x border-x-black border-b-[6px] border-b-[#050505] shadow-[0_6px_15px_rgba(0,0,0,0.5)] hover:brightness-110'}`}
                >
                  <span className="text-lg">Tails</span>
                </button>
              </div>
            </div>

            {gameMode === 'solo' ? (
              <button 
                onClick={handleSoloPlay} 
                disabled={isProcessing || !uid || parseInt(wager) > credits} 
                className="w-full mt-4 py-6 bg-gradient-to-b from-white to-[#d4d4d8] text-black font-black uppercase tracking-[0.2em] text-sm rounded-2xl border-t-2 border-white border-x border-x-[#a1a1aa] border-b-[6px] border-b-[#71717a] shadow-[0_6px_20px_rgba(255,255,255,0.3)] hover:brightness-110 btn-push disabled:opacity-50 disabled:grayscale transition-all flex items-center justify-center gap-3"
              >
                <Play fill="black" size={18} /> Flip Against House
              </button>
            ) : (
              <button 
                onClick={handleCreateMatch} 
                disabled={isProcessing || !uid || parseInt(wager) > credits} 
                className="w-full mt-4 py-6 bg-gradient-to-b from-white to-[#d4d4d8] text-black font-black uppercase tracking-[0.2em] text-sm rounded-2xl border-t-2 border-white border-x border-x-[#a1a1aa] border-b-[6px] border-b-[#71717a] shadow-[0_6px_20px_rgba(255,255,255,0.3)] hover:brightness-110 btn-push disabled:opacity-50 disabled:grayscale transition-all"
              >
                Create PVP Table
              </button>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col">
          {gameMode === 'multiplayer' ? (
            <>
              <h3 className="text-xl font-black italic uppercase tracking-widest text-white mb-6 flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-600 animate-pulse shadow-[0_0_15px_rgba(220,38,38,0.8)]"></div> Live PVP Tables
              </h3>
              
              <div className="flex-1 space-y-4">
                {lobbies.length === 0 ? (
                  <div className="border-2 border-dashed border-red-900/30 bg-[#050000]/40 rounded-3xl p-12 text-center flex flex-col items-center justify-center h-64">
                    <CircleDashed size={40} className="text-red-900/50 mb-4" />
                    <p className="text-zinc-600 font-mono text-xs uppercase tracking-widest">No active PVP wagers. Create one or switch to Solo Mode.</p>
                  </div>
                ) : (
                  lobbies.map((l: any) => (
                    <div key={l.id} className="bg-gradient-to-b from-[#180000] to-black border border-red-900/30 p-5 rounded-2xl group hover:border-red-600/60 transition-all flex items-center justify-between shadow-xl">
                      <div className="flex items-center gap-6">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center font-black text-2xl border shadow-inner ${l.creatorSide === 'heads' ? 'bg-red-900/30 border-red-600/50 text-red-500' : 'bg-zinc-900/50 border-zinc-700/50 text-white'}`}>
                          {l.creatorSide === 'heads' ? 'H' : 'T'}
                        </div>
                        <div>
                          <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">{l.creatorName}'s Wager</div>
                          <div className="text-3xl font-black text-white italic tracking-tighter">{l.wagerAmount.toLocaleString()} <span className="text-red-600 text-sm">CRD</span></div>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleJoinMatch(l)} 
                        disabled={isProcessing || !uid || l.creatorId === uid || credits < l.wagerAmount} 
                        className="px-8 py-4 bg-gradient-to-b from-red-600 to-red-800 text-white font-black uppercase tracking-widest text-[11px] rounded-xl btn-push disabled:opacity-30 flex items-center gap-2 border-t border-red-500 border-b-[4px] border-b-red-950 shadow-[0_4px_15px_rgba(220,38,38,0.4)] hover:brightness-110"
                      >
                        {l.creatorId === uid ? 'Waiting...' : 'Challenge'} <ChevronRight size={16} className={l.creatorId === uid ? 'hidden' : ''}/>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="bg-[#050505]/90 backdrop-blur-2xl border border-red-900/30 rounded-[2rem] p-8 flex flex-col justify-between h-full shadow-[0_20px_50px_rgba(220,38,38,0.15)]">
              <div>
                <h3 className="text-xl font-black italic uppercase tracking-widest text-white mb-4 flex items-center gap-3">
                  <Bot className="text-red-600" size={24} /> Casino House Rules
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 font-medium">
                  Experience instantaneous 3D flips directly against the Casino Vault. Select your stake, pick Heads or Tails, and launch the high-roller solid gold coin.
                </p>

                <div className="grid grid-cols-2 gap-4 my-6">
                  <div className="bg-gradient-to-b from-[#180000] to-black border border-red-900/50 p-5 rounded-2xl shadow-inner">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold block mb-1">Standard Payout</span>
                    <span className="text-2xl font-black text-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]">2x DOUBLE</span>
                  </div>
                  <div className="bg-gradient-to-b from-[#18181b] to-black border border-zinc-800 p-5 rounded-2xl shadow-inner">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold block mb-1">Physics Engine</span>
                    <span className="text-2xl font-black text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">3D WebGL</span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-gradient-to-r from-red-900/20 to-transparent border-l-4 border-red-600 flex items-center gap-4">
                <Flame className="text-red-500 shrink-0" size={28} />
                <p className="text-xs text-zinc-300 font-bold leading-relaxed tracking-wide">
                  Want human competition? Switch to <span className="text-red-500">VS PLAYERS</span> mode to broadcast open tables to challengers worldwide!
                </p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );

  const renderActiveGame = () => {
    if (!match) return null;
    const isWinner = match.winnerId === uid;

    return (
      <div className="absolute inset-0 z-30 flex flex-col pointer-events-none">
        
        {view === 'waiting' && (
          <div className="flex-1 flex items-center justify-center animate-in zoom-in-95 bg-black/80 backdrop-blur-md pointer-events-auto">
            <div className="bg-gradient-to-b from-[#180000] to-black border border-red-900/50 p-12 rounded-[3rem] text-center shadow-[0_40px_100px_rgba(220,38,38,0.2)] w-full max-w-md">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 border-4 border-red-900/30 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                <Coins className="absolute inset-0 m-auto text-red-500" size={32} />
              </div>
              <h2 className="text-3xl font-black uppercase tracking-tighter text-white mb-2 italic">Awaiting Challenger</h2>
              <p className="text-red-500 font-bold tracking-widest uppercase text-sm mb-8">{match.wagerAmount.toLocaleString()} CRD on {match.creatorSide}</p>
              <button onClick={handleCancelLobby} className="px-8 py-4 bg-gradient-to-b from-[#27272a] to-[#18181b] border-t border-[#3f3f46] border-b-[4px] border-b-black text-zinc-400 font-black uppercase tracking-widest text-[10px] rounded-full hover:brightness-110 btn-push transition-all">
                Cancel Wager
              </button>
            </div>
          </div>
        )}

        {view === 'matchup' && (
          <div className="absolute inset-0 z-50 bg-[#000000]/95 flex items-center justify-center animate-in fade-in duration-200">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(220,38,38,0.2),transparent_70%)]"></div>
            
            <div className="w-full flex items-center justify-between px-8 md:px-24 relative z-10">
              <div className="text-center animate-in slide-in-from-left-full duration-500 flex-1">
                <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-widest mb-4 font-bold">{match.creatorName}</p>
                <div className={`text-6xl md:text-9xl font-black uppercase italic tracking-tighter ${match.creatorSide === 'heads' ? 'text-glow-red text-red-600' : 'text-zinc-200 drop-shadow-[0_0_20px_rgba(255,255,255,0.3)]'}`}>
                  {match.creatorSide.charAt(0)}
                </div>
              </div>

              <div className="px-4 animate-in zoom-in-50 duration-300 delay-300 fill-mode-both shrink-0 animate-heartbeat">
                <span className="text-white font-black italic text-6xl md:text-8xl tracking-tighter drop-shadow-[0_0_20px_rgba(255,255,255,0.6)]">VS</span>
              </div>

              <div className="text-center animate-in slide-in-from-right-full duration-500 flex-1">
                <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-widest mb-4 font-bold">{match.joinerName}</p>
                <div className={`text-6xl md:text-9xl font-black uppercase italic tracking-tighter ${match.joinerSide === 'heads' ? 'text-glow-red text-red-600' : 'text-zinc-200 drop-shadow-[0_0_20px_rgba(255,255,255,0.3)]'}`}>
                  {match.joinerSide.charAt(0)}
                </div>
              </div>
            </div>

            <div className="absolute bottom-20 inset-x-0 text-center animate-in slide-in-from-bottom-10 duration-500 delay-500 fill-mode-both">
              <p className="text-white text-4xl md:text-5xl font-black tracking-widest uppercase italic">
                POT: <span className="text-red-500">{match.pot.toLocaleString()} CRD</span>
              </p>
            </div>
          </div>
        )}

        {view === 'flipping' && (
          <div className="absolute top-10 inset-x-0 z-10 flex justify-center pointer-events-none animate-in fade-in slide-in-from-top-10">
            <div className="bg-[#050000]/90 backdrop-blur-xl px-12 py-5 rounded-full border border-red-900/50 flex items-center gap-10 shadow-[0_10px_30px_rgba(220,38,38,0.2)]">
              <span className={`font-black uppercase text-xl ${match.creatorSide === 'heads' ? 'text-red-500' : 'text-white'}`}>{match.creatorName}</span>
              <span className="text-white font-black italic text-2xl tracking-tighter opacity-50">VS</span>
              <span className={`font-black uppercase text-xl ${match.joinerSide === 'heads' ? 'text-red-500' : 'text-white'}`}>{match.joinerName}</span>
            </div>
          </div>
        )}

        {view === 'result' && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#000000]/85 backdrop-blur-lg animate-in fade-in duration-500 pointer-events-auto">
            <div className="text-center p-12 md:p-24 rounded-[3rem] animate-in zoom-in-90 duration-700 spring relative w-full max-w-2xl">
              
              <div className={`absolute inset-0 blur-[120px] -z-10 rounded-full opacity-40 ${isWinner ? 'bg-red-600' : 'bg-zinc-800'}`}></div>
              
              <p className="text-[10px] font-mono uppercase tracking-[0.5em] mb-6 text-zinc-400 font-bold">
                Result: <span className="text-white">{match.resultSide}</span>
              </p>
              
              <h2 className={`text-6xl md:text-8xl font-black uppercase italic tracking-tighter drop-shadow-2xl mb-8 ${isWinner ? 'text-glow-red text-red-500' : 'text-zinc-600'}`}>
                {isWinner ? 'YOU WIN' : 'YOU LOSE'}
              </h2>
              
              <div className="mb-12 h-20 flex items-center justify-center">
                {isWinner ? (
                  <p className="text-4xl font-black text-white flex items-center justify-center gap-4 bg-red-900/30 px-10 py-5 rounded-full border border-red-500/50 shadow-[0_0_30px_rgba(220,38,38,0.4)] backdrop-blur-md">
                    <Trophy className="text-red-400" size={36} />
                    +{match.pot.toLocaleString()} <span className="text-red-500 text-2xl">CRD</span>
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-zinc-500 uppercase tracking-widest border border-zinc-800 bg-black/50 px-8 py-4 rounded-full">
                    Wager lost to {match.winnerId === match.creatorId ? match.creatorName : match.joinerName}
                  </p>
                )}
              </div>

              <div className="flex gap-4 justify-center">
                {match.isSolo && (
                  <button 
                    onClick={() => { triggerMatchup(match); }}
                    className="px-10 py-5 bg-gradient-to-b from-[#ff3333] to-[#cc0000] text-white font-black uppercase tracking-[0.2em] rounded-2xl border-t-2 border-[#ff8888] border-x border-x-[#990000] border-b-[6px] border-b-[#660000] shadow-[0_6px_20px_rgba(220,38,38,0.5)] hover:brightness-110 btn-push text-xs md:text-sm flex items-center gap-2 transition-all"
                  >
                    <RefreshCw size={18} /> Play Again
                  </button>
                )}
                <button 
                  onClick={() => { setActiveMatchId(null); setMatch(null); setView('lobby'); }}
                  className="px-12 py-5 bg-gradient-to-b from-white to-[#d4d4d8] text-black font-black uppercase tracking-[0.2em] rounded-2xl border-t-2 border-white border-x border-x-[#a1a1aa] border-b-[6px] border-b-[#71717a] shadow-[0_6px_20px_rgba(255,255,255,0.3)] hover:brightness-110 btn-push text-xs md:text-sm transition-all"
                >
                  Return to Lobby
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[400] bg-[#000000] flex items-center justify-center animate-in fade-in duration-200 casino-font">
      <GameStyles />

      <div className="w-full h-full flex flex-col overflow-hidden relative">
        
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div ref={mountRef} className="absolute inset-0" />
          <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(rgba(220,38,38,1)_1px,transparent_1px),linear-gradient(90deg,rgba(220,38,38,1)_1px,transparent_1px)] bg-[size:100px_100px]" />
          <div className="absolute inset-0 shadow-[inset_0_0_200px_rgba(0,0,0,1)]" />
        </div>

        {/* 🔥 TOP BAR - Back button left, Balance right */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-3 sm:px-4 py-3 bg-[#050000]/80 backdrop-blur-xl">
          <button
            onClick={onClose}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-black/60 border border-red-500/30 rounded-xl text-white hover:bg-red-500/20 hover:border-red-400 transition-all duration-300 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest">Back</span>
          </button>

          <div className="flex items-center gap-2 bg-black/60 border border-red-500/30 rounded-xl px-3 sm:px-4 py-2">
            <Coins size={16} className="text-yellow-400" />
            <span className="text-white font-bold font-mono text-xs sm:text-sm">{credits} CRD</span>
          </div>
        </div>

        <div className="flex-1 overflow-hidden relative z-10 pointer-events-none mt-14">
          {activeMatchId ? renderActiveGame() : renderLobby()}
        </div>

      </div>
    </div>
  );
};

export default Zwepz;